/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getAttributeValuesAsync, getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    buildCacheKey,
    generateImageUrl,
    getProductUrlSync,
    getSelectedProductIdFromActionInput
} from '@msdyn365-commerce-modules/retail-actions';

import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
import { IProductPageSummaryConfig } from './product-page-summary.props.autogenerated';

/**
 * Product Page Summary Input
 */
export class ProductPageSummaryInput implements IActionInput {
    public apiSettings: ICommerceApiSettings;

    public config: IProductPageSummaryConfig;

    public productId: number;

    public locale: string;

    constructor(config: IProductPageSummaryConfig, apiSettings: ICommerceApiSettings, productId: number, locale: string) {
        this.config = config || {};
        this.apiSettings = apiSettings;
        this.productId = productId;
        this.locale = locale;
    }

    public getCacheObjectType = (): string => 'ProductPageSummary';

    public getCacheKey = (): string =>
        `${buildCacheKey('ProductPageSummary', this.apiSettings)}-${this.config.title}-${this.productId}-${this.locale}`;

    public dataCacheType = (): CacheType => 'application';
}

export interface IProductPageSummary extends IPageSummaryData {
    price?: number;
    productId?: number;
    productBrand?: string;
}

const createInput = (args: ICreateActionContext) => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new ProductPageSummaryInput(
            <IProductPageSummaryConfig>args.config,
            args.requestContext.apiSettings,
            +productId,
            args.requestContext.locale
        );
    }
    throw new Error('Unable to create ProductPageSummaryAction input, no productId found on module config or query');
};

// Checks product attributes for the presence of brand attribute to extract the brand name for product metadata
const getProductBrand = async (
    context: IActionContext,
    recordId: number,
    channelId: number,
    catalogId: number
): Promise<string | undefined> => {
    const productAttributes = await getAttributeValuesAsync(
        { callerContext: context, queryResultSettings: {} },
        recordId,
        channelId,
        catalogId
    );

    for (const productAttribute of productAttributes) {
        if (productAttribute.Name === 'Brand') {
            return productAttribute.TextValue;
        }
    }
    return;
};

const action = async (input: ProductPageSummaryInput, context: IActionContext): Promise<IProductPageSummary> => {
    const { config, productId, apiSettings } = input;
    let simpleProductData;
    try {
        simpleProductData = await getByIdAsync({ callerContext: context }, productId, apiSettings.channelId);
    } catch {
        // Do nothing, if there's an error we fall back to values defined from config
    }
    if (simpleProductData) {
        let productUrl: string | undefined;
        try {
            productUrl = getProductUrlSync(simpleProductData, context);
            const canonicalDomain = context.requestContext.canonicalDomain;
            if (productUrl && canonicalDomain) {
                productUrl = `https://${canonicalDomain}${productUrl}`.toLocaleLowerCase();
            } else {
                productUrl = undefined;
            }
        } catch {
            productUrl = undefined;
        }

        let productBrand = simpleProductData?.ExtensionProperties?.find(ep => ep.Key === 'Brand')?.Value?.StringValue;
        if (!productBrand) {
            try {
                productBrand = await getProductBrand(context, productId, apiSettings.channelId, apiSettings.catalogId);
            } catch {
                productBrand = undefined;
            }
        }

        return {
            title: simpleProductData.Name,
            description: simpleProductData.Description,
            sharingImageUrl: generateImageUrl(simpleProductData.PrimaryImageUrl, apiSettings),
            canonicalUrl: productUrl,
            faviconUrl: config && config.faviconUrl,
            productId: simpleProductData.RecordId,
            price: simpleProductData.AdjustedPrice,
            productBrand
        };

        // If the action fails fallback to values defined from config
    } else if (config) {
        return {
            title: config.title,
            description: config.description,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/product-page-summary',
    action: <IAction<IProductPageSummary>>action,
    input: createInput
});
